import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface CommentWithProfile {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles: {
    id: string;
    username: string;
    avatar_url: string | null;
  } | null;
}

export const useComments = (postId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  // Fetching Logic
  const { data: comments = [], isLoading, refetch } = useQuery({
    queryKey: ['post-comments', postId],
    queryFn: async () => {
      if (!postId) return [];
      
      // Fetch comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('id, content, created_at, user_id')
        .eq('post_id', postId)
        .order('created_at', { ascending: false });
      
      if (commentsError) throw commentsError;
      if (!commentsData || commentsData.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(commentsData.map(c => c.user_id))];

      // Fetch profiles for comment authors
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      // Combine comments with profiles
      const commentsWithProfiles: CommentWithProfile[] = commentsData.map(comment => ({
        ...comment,
        profiles: profilesData?.find(p => p.id === comment.user_id) || null
      }));
      
      return commentsWithProfiles;
    },
    enabled: !!postId,
  });

  // Posting Logic
  const { mutate: postComment, isPending: isPosting } = useMutation({
    mutationFn: async (commentText: string) => {
      if (!user) throw new Error('User not authenticated');
      if (!postId) throw new Error('No post ID provided');
      
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: commentText,
        })
        .select('id, content, created_at, user_id')
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (newComment) => {
      // Optimistically add new comment to the list
      if (newComment && profile) {
        const newCommentWithProfile: CommentWithProfile = {
          ...newComment,
          profiles: {
            id: profile.id,
            username: profile.username,
            avatar_url: profile.avatar_url
          }
        };
        
        queryClient.setQueryData<CommentWithProfile[]>(
          ['post-comments', postId],
          (old = []) => [newCommentWithProfile, ...old]
        );
      }
      
      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: ['post-comments', postId] });
      toast.success('Comment posted!');
    },
    onError: (error) => {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    },
  });

  const commentCount = comments.length;

  return { 
    comments, 
    isLoading, 
    postComment, 
    isPosting,
    commentCount,
    refetch
  };
};
